import { useEffect, useState } from "react";
import type { OrderBook } from "../../types/api";
import * as marketService from "../../services/marketService";
import { Skeleton } from "../ui/skeleton";

interface DepthOfMarketProps {
  symbol: string;
  maxEntries?: number;
}

/**
 * Composant Depth of Market (DOM) - Carnet d'ordres
 */
export const DepthOfMarket = ({
  symbol,
  maxEntries = 10,
}: DepthOfMarketProps) => {
  const [orderBook, setOrderBook] = useState<OrderBook | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentPrice, setCurrentPrice] = useState<number | null>(null);

  useEffect(() => {
    let isMounted = true;
    let intervalId: NodeJS.Timeout | null = null;

    const loadData = async () => {
      if (!isMounted) return;

      try {
        const data = await marketService.getOrderBook(symbol, maxEntries * 2);
        if (!isMounted) return;

        setOrderBook(data);
        // Calculer le prix actuel comme la moyenne entre le meilleur bid et ask
        if (data.bids.length > 0 && data.asks.length > 0) {
          const midPrice = (data.bids[0].price + data.asks[0].price) / 2;
          setCurrentPrice(midPrice);
        }
        setLoading(false);
      } catch (error) {
        console.error("Erreur lors du chargement du carnet d'ordres:", error);
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    // Chargement initial
    setLoading(true);
    void loadData();

    // Rafraîchir toutes les 5 secondes (réduit de 2s à 5s pour moins de requêtes)
    intervalId = setInterval(() => {
      if (isMounted) {
        void loadData();
      }
    }, 5000);

    return () => {
      isMounted = false;
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [symbol, maxEntries]);

  if (loading || !orderBook) {
    return <Skeleton className="h-full w-full" />;
  }

  const bids = orderBook.bids.slice(0, maxEntries);
  const asks = orderBook.asks.slice(0, maxEntries);
  const maxQuantity = Math.max(
    ...bids.map((b) => b.quantity),
    ...asks.map((a) => a.quantity),
  );

  return (
    <div className="flex h-full w-full flex-col rounded-2xl border border-white/5 bg-gradient-to-br from-slate-900/50 to-slate-800/30 p-4 shadow-lg shadow-black/20 backdrop-blur-sm">
      <div className="mb-4 flex-shrink-0">
        <h3 className="text-lg font-semibold text-slate-100">DOM</h3>
        {currentPrice && (
          <p className="text-2xl font-bold tabular-nums text-sky-200/95">
            {currentPrice.toLocaleString("fr-FR", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 8,
            })}
          </p>
        )}
      </div>

      <div className="scrollbar-thin flex-1 space-y-1 overflow-y-auto pr-2">
        {/* Ordres de vente (asks) - tons rouges adoucis, meilleur contraste */}
        <div className="space-y-0.5">
          {asks.reverse().map((ask, index) => (
            <div
              key={`ask-${index}`}
              className="relative flex items-center justify-between rounded px-2 py-1 text-sm transition-all hover:bg-white/5">
              <div
                className="absolute right-0 h-full rounded bg-gradient-to-r from-transparent to-rose-500/10"
                style={{
                  width: `${(ask.quantity / maxQuantity) * 100}%`,
                }}
              />
              <span className="relative z-10 font-medium tabular-nums text-rose-200/95">
                {ask.price.toLocaleString("fr-FR", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 8,
                })}
              </span>
              <span className="relative z-10 tabular-nums text-slate-200/90">
                {ask.quantity.toLocaleString("fr-FR", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 8,
                })}
              </span>
            </div>
          ))}
        </div>

        {/* Prix actuel au centre */}
        {currentPrice && (
          <div className="my-2 border-t border-white/10 py-2 text-center">
            <p className="text-lg font-bold tabular-nums text-sky-200/95">
              {currentPrice.toLocaleString("fr-FR", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 8,
              })}
            </p>
          </div>
        )}

        {/* Ordres d'achat (bids) */}
        <div className="space-y-0.5">
          {bids.map((bid, index) => (
            <div
              key={`bid-${index}`}
              className="relative flex items-center justify-between rounded px-2 py-1 text-sm transition-all hover:bg-white/5">
              <div
                className="absolute left-0 h-full rounded bg-gradient-to-r from-emerald-500/10 to-transparent"
                style={{
                  width: `${(bid.quantity / maxQuantity) * 100}%`,
                }}
              />
              <span className="relative z-10 tabular-nums text-slate-200/90">
                {bid.quantity.toLocaleString("fr-FR", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 8,
                })}
              </span>
              <span className="relative z-10 font-medium tabular-nums text-emerald-200/95">
                {bid.price.toLocaleString("fr-FR", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 8,
                })}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
